import { isObject } from "@turf/turf";
import {
  removeSearchResultDetailsAction, setCurrentPageAction, showProductDetailsAction,
  storeFormDataPayloadAction, storeSearchResultDetailsAction, toggleFirstPartSidebarAction,
  toggleSecondPartSidebarAction,
  toggleSidebarProcessingParametersAction
} from "../application/actions";
import {
  searchResultsErrorAction,
  searchStartedAction,
  searchResultsSuccessAction,
  updateSelectedProductsAction,
  updateIsInterferometricStackSearchAction,
  removeAllSelectedProductsAction,
  removeAllInterferometricStacks
} from "./actions";

import ApplicationService from "../../services/ApplicationService"
import {ProductImageStacType, SearchResultElementType} from "../../types/product";
import {
  getProductImagesDifference
} from "../../helpers/productHelper";
import {ConfigurationsType} from "../../types/configurations";
import {hideAllQuicklookPreviewsAction, removeKMLGuideShapeAction} from "../geoMap/actions";
import {ShapeType} from "../../types/shape";
import {isSelectableProductImage} from "../../helpers/searchResultsHelper";
import {successToastNotification, warningToastNotification} from "../../helpers/toastNotificationHelper";
import {getErrorMessageFrom} from "../../helpers/errorsHelper";
import translations from "../../translations";
import {FIRST_SEARCH_PAGE_NUMBER} from "../../helpers/searchHelper";

/** Get masterIndex in features array */
const getMasterIndex = (
  features: {[key: string]: any}[],
  currentMasterItem: {[key: string]: any},
): number | null => {
  const masterIndex = features.findIndex((feature) => feature?.id === currentMasterItem?.id);
  if (masterIndex === -1) return null;

  return masterIndex;
};

export const searchDispatchAction = (
  formData: any,
  isNewSearch: boolean,
  isInterferometricStackSearch: boolean,
  language: string,
  searchByProducts: boolean,
  searchProductIds
) => (dispatch) => (
  isNewSearch
    ? dispatch(newSearchDataDispatchAction(formData, isInterferometricStackSearch, language, searchByProducts, searchProductIds))
    : dispatch(searchDataDispatchAction(formData, isInterferometricStackSearch, language, searchByProducts, searchProductIds))
);

export const invalidateReprocessSearchDispatchAction = (
  formData: any,
  language: string,
) => async (dispatch, getState) => {
  await dispatch(storeFormDataPayloadAction(formData));
  const state = getState();

  await dispatch(searchStartedAction());
  let isSearchResultsSuccessfully = false;
  try {
    const newFormData = {...formData}; // uso una copia, per non alterarlo

    // chiamo il servizo di ricerca
    const {data} = await ApplicationService.invalidateReprocessSearchData(newFormData);

    const newdata = {
      ...data,
      items: data.features,
    }
    delete newdata.features;

    // ricerca andata a buon fine
    if (
      newdata.totalItems === 0 || (!newdata.items || newdata.items.length === 0)
    ) {
      // non ho risultati
      await dispatch(searchResultsErrorAction(translations("noDataDispatchAction", language)));
    } else {
      await dispatch(searchResultsSuccessAction(newdata));
      isSearchResultsSuccessfully = true;
    }
  } catch (error) {
    switch (true) {
      default:
        await dispatch(searchResultsErrorAction(`Error: ${getErrorMessageFrom(error)}`));
    }
  }

  return isSearchResultsSuccessfully;
}

const newSearchDataDispatchAction = (formData, isInterferometricStackSearch, language: string, searchByProducts, searchProductIds) => async (dispatch) => {
  await dispatch(clearProductSelectionsDispatchAction());
  await dispatch(removeAllInterferometricStacks());
  const isSearchResultsSuccessfully = await dispatch(searchDataDispatchAction(formData, isInterferometricStackSearch, language, searchByProducts, searchProductIds));

  return dispatch(toggleFirstPartSidebarAction(!isSearchResultsSuccessfully));
};
const searchDataDispatchAction = (formData, isInterferometricStackSearch, language: string, searchByProducts = false, searchProductIds) => async (dispatch, getState) => {
  await dispatch(storeFormDataPayloadAction(formData));
  await dispatch(toggleSidebarProcessingParametersAction(false));
  await dispatch(toggleSecondPartSidebarAction(true));
  await dispatch(updateIsInterferometricStackSearchAction(isInterferometricStackSearch));
  // await dispatch(removeKMLShapeAction());

  const state = getState();

  await dispatch(searchStartedAction());

  // gestione del contatore di current
  if (!formData.hasOwnProperty("token") || !formData.token) {
    // devo resettare la pagina ad 1
    await dispatch(setCurrentPageAction(FIRST_SEARCH_PAGE_NUMBER));
  } else if (formData.token && formData.token.split(":")[0] === "next") {
    // devo fare pagina ++
    await dispatch(setCurrentPageAction(state.application.currentPage + 1));
  } else if (formData.token && formData.token.split(":")[0] === "prev") {
    // devo fare pagina --
    await dispatch(setCurrentPageAction(state.application.currentPage - 1));
  }

  let isSearchResultsSuccessfully = false;
  try {
    // logic to handle multiple product ids
    if (searchByProducts) {
      // serve per cancellare i filtri in caso di ricerca per productId
      Object.keys(formData.filters).forEach((filterKey) => {
        if (filterKey === "product_identifier") {
          return;
        }
        delete formData.filters[filterKey];
      });

      if (formData.routine) { // per gestire il caso tile id
        formData.filters.tiles_id = {
          value: searchProductIds,
          field:
              {
                description: "Tiles ID",
                type: "string",
                name: "productId",
                sequence: 9,
                placeholder: "Insert a list of Tile IDs, e.g. wsdf_00_dsdw_sds8, acdf_10_dsdw_sds9",
                isApplicable: true,
                isMandatory: false,
                operator: "eq",
                search: true,
                cartName: "tiles_id",
              }
        };
        successToastNotification(`${translations("tileIDsSelectedMessage", language)}`);
      } else { // per gestire il caso product identifier
        formData.ids = searchProductIds;
        successToastNotification(`${translations("productIdentifiersSelectedMessage", language)}`);
      }
      // queste in realtà non dovrebbero mai arrivare adesso
      delete formData.polygon;
      delete formData.circle;
      // le date possono ora essere passate
      // delete formData.from;
      // delete formData.to;
    }

    const newFormData = {...formData}; // uso una copia, per non alterarlo

    // gestione parametri baseline ortogonale
    if (newFormData.baselineFilter && newFormData.masterId && newFormData.maxBaseline) {
      newFormData.filters.interferometric_baseline = newFormData.maxBaseline;
      newFormData.filters.interferometric_masters = [newFormData.masterId];
      // devo passare gli ids
      if (newFormData.slaveId && newFormData.slaveId.length) {
        newFormData.ids = [newFormData?.ids ? newFormData.ids : [], newFormData.slaveId]
          .flat().reduce((acc: string[] = [], id: string): string[] => {
            if (acc.includes(id)) return acc;
            return [...acc, id];
          }, []); // aggiungo tutti gli slave selezionati
      }
    } else {
      // Remove baseline-filter data
      delete newFormData.filters.interferometric_baseline;
      delete newFormData.filters.interferometric_masters;
    }

    // chiamo il servizio di ricerca
    const {data} = await ApplicationService.searchData(newFormData, isInterferometricStackSearch);

    if (formData.baselineFilter && formData.masterId && formData.maxBaseline && state.application.currentMasterItem) {
      const isValidArray = Array.isArray(data?.features);
      const isValidObject = isObject(state?.application?.currentMasterItem);

      const masterIndex = isValidArray && isValidObject
        ? getMasterIndex(data?.features, state?.application?.currentMasterItem)
        : null;

      if (typeof masterIndex === "number") {
        data.features = [{...data.features[masterIndex], isMasterItem: true}, ...data.features.filter((x, i) => i !== masterIndex)];
      } else {
        // Handle the absence of masterItem inside data.features
        data.features.unshift({
          ...state.application.currentMasterItem, isMasterItem: true
        });
      }
    }

    const newdata = {
      ...data,
      pageSize: data.page_size ? data.page_size : 1,
      totalPages: data.total_pages ? data.total_pages : 1,
      totalItems: data.total_items ? data.total_items : data.features.length,
      items: data.features,
    }

    delete newdata.features;
    delete newdata.page_size;
    delete newdata.total_pages;
    delete newdata.total_items;

    // mi costruisco la risposta con gli stack
    if (isInterferometricStackSearch) {
      newdata.stacks = [];
      const foundStacks: any = {};
      data.features.forEach((feature) => {
        if (!foundStacks.hasOwnProperty(feature.properties.interferometric_stack_id)) {
          foundStacks[feature.properties.interferometric_stack_id] = {
            items: [],
            acquisition_mode: feature.properties.acquisition_mode,
            identifier: feature.properties.beam,
            mission: feature.properties.mission,
            orbit_direction: feature.properties.orbit_direction,
            polarization: feature.properties.polarization
          };
        }
        foundStacks[feature.properties.interferometric_stack_id].items.push(feature);
      });
      Object.keys(foundStacks).forEach((stackKey) => {
        newdata.stacks.push(foundStacks[stackKey]);
      });
      delete newdata.items;
    }

    // ricerca andata a buon fine
    if (
      newdata.totalItems === 0 ||
      (!isInterferometricStackSearch && newdata.items && newdata.items.length === 0) ||
      (isInterferometricStackSearch && newdata.stacks && newdata.stacks.length === 0)
    ) {
      // non ho risultati
      await dispatch(searchResultsErrorAction(translations("noDataDispatchAction", language)));
    } else {
      await dispatch(searchResultsSuccessAction(newdata));
      dispatch(removeKMLGuideShapeAction());
      isSearchResultsSuccessfully = true;
    }
  } catch (error) {
    switch (true) {
      default:
        await dispatch(searchResultsErrorAction(`Error: ${getErrorMessageFrom(error)}`));
    }
  }

  return isSearchResultsSuccessfully;
};

export const updateSelectedProductsDispatchAction = (
  configurations: ConfigurationsType,
  searchFormValues,
  selectedArea: ShapeType,
  isInterferometricStackSearch: boolean,
  products: ProductImageStacType[],
  isAddingProducts: boolean = false,
  language
) => async (dispatch, getState) => {
  const {
    application: {
      sidebarProcessingParametersShown,
    },
    product: {
      selectedProducts,
      allInterferometricStacks
    },
    cart: {
      ordersSubmitted
    }
  } = getState();

  let newProductSelection: ProductImageStacType[] = products.filter((product) => {
    return isSelectableProductImage(configurations, ordersSubmitted, product)
  });
  if (isAddingProducts) {
    // add only new selectable products
    newProductSelection = selectedProducts.concat(getProductImagesDifference(newProductSelection, selectedProducts));
  } else {
    // remove
    newProductSelection = getProductImagesDifference(selectedProducts, newProductSelection);
  }

  return dispatch(updateSelectedProductsAction(newProductSelection));
};

export const openSearchResultDetailsDispatchAction = (
  searchResultDetails: SearchResultElementType,
) => async (dispatch) => {
  await dispatch(storeSearchResultDetailsAction(searchResultDetails));
  return dispatch(showProductDetailsAction());
};

export const clearProductSelectionsDispatchAction = () => async (dispatch) => {
  await dispatch(hideAllQuicklookPreviewsAction());
  await dispatch(removeSearchResultDetailsAction());
  await dispatch(toggleSidebarProcessingParametersAction(false));
  return dispatch(removeAllSelectedProductsAction());
};
